import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Avatar,
  Chip,
  Stack,
  Divider,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

interface EmailDetailsModalProps {
  open: boolean;
  onClose: () => void;
  email: any | null;
}

const formatDate = (iso?: string) =>
  iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '-';

export const EmailDetailsModal: React.FC<EmailDetailsModalProps> = ({ open, onClose, email }) => {
  const navigate = useNavigate();
  if (!email) return null;

  const prospectName =
    email?.prospect?.full_name ||
    ((email?.prospect?.first_name || email?.prospect?.last_name)
      ? `${email?.prospect?.first_name || ''} ${email?.prospect?.last_name || ''}`.trim()
      : undefined) ||
    email?.prospect?.name ||
    '-';

  const prospectAvatar = email?.prospect?.profile_image || email?.prospect?.profile_image_url || undefined;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Email Details</DialogTitle>
      <DialogContent dividers sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
          <Avatar src={prospectAvatar} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" noWrap>{prospectName}</Typography>
            {email.stream?.name && (
              <Typography variant="caption" color="text.secondary" noWrap>{email.stream?.name}</Typography>
            )}
          </Box>
          <Chip size="small" label={email.type || '-'} />
          {email.status && <Chip size="small" label={email.status} variant="outlined" />}
        </Stack>

        <Stack spacing={0.5} sx={{ mb: 1.5 }}>
          <Typography variant="body2"><b>Subject:</b> {email.subject || '-'}</Typography>
          <Typography variant="body2"><b>To:</b> {email.to || '-'}</Typography>
          <Typography variant="body2"><b>From:</b> {[email.from_name, email.from_email].filter(Boolean).join(' <') + (email.from_email ? '>' : '') || '-'}</Typography>
          <Typography variant="body2"><b>Send Date:</b> {formatDate(email.send_date)}</Typography>
        </Stack>

        <Divider sx={{ my: 1 }} />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>Message</Typography>
        <Box sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          p: 2,
          maxHeight: { xs: 300, sm: 480 },
          overflow: 'auto',
          bgcolor: 'background.paper',
          width: '100%',
          overflowWrap: 'anywhere',
        }}
          // Note: content is assumed trusted from backend. Consider sanitizing if necessary.
          dangerouslySetInnerHTML={{ __html: email.message || '' }}
        />
      </DialogContent>
      <DialogActions>
        {email?.prospect?.key && (
          <Button 
            variant="outlined" 
            onClick={() => {
              onClose();
              navigate(`/leads/${email.prospect.key}`);
            }}
          >
            Open Lead
          </Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailDetailsModal;


